import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Coins, Wallet, PieChart, DollarSign } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import BudgetForm from './FamilyBudgetCalculatorPage/components/BudgetForm';
import BudgetOverview from './FamilyBudgetCalculatorPage/components/BudgetOverview';
import IncomeExpenseBreakdown from './FamilyBudgetCalculatorPage/components/IncomeExpenseBreakdown';
import BudgetTips from './FamilyBudgetCalculatorPage/components/BudgetTips';
import QuickActionsBar from './FamilyBudgetCalculatorPage/components/QuickActionsBar';
import FinancialGoalsSidebar from './FamilyBudgetCalculatorPage/components/FinancialGoalsSidebar';
import SavedBudgetsSidebar from './FamilyBudgetCalculatorPage/components/SavedBudgetsSidebar';
import TrendingSidebar from './FamilyBudgetCalculatorPage/components/TrendingSidebar';
import InfoContent from './FamilyBudgetCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  // Основная информация
  budgetName: 'Мой семейный бюджет',
  budgetType: 'family', // family, personal, couple
  budgetPeriod: 'monthly', // monthly, yearly
  familyMembers: 2,
  hasChildren: false,
  childrenCount: 0,
  region: 'moscow', // Московский регион по умолчанию
  
  // Доходы
  incomes: [
    { id: '1', name: 'Основная зарплата', amount: '90000', category: 'salary', recurring: true, frequency: 'monthly' },
    { id: '2', name: 'Дополнительная работа', amount: '15000', category: 'side_job', recurring: false, frequency: 'monthly' }
  ],
  
  // Расходы
  expenses: [
    { id: '1', name: 'Аренда жилья / Ипотека', amount: '30000', category: 'housing', recurring: true, frequency: 'monthly', essential: true },
    { id: '2', name: 'Коммунальные платежи', amount: '5000', category: 'utilities', recurring: true, frequency: 'monthly', essential: true },
    { id: '3', name: 'Продукты питания', amount: '20000', category: 'groceries', recurring: true, frequency: 'monthly', essential: true },
    { id: '4', name: 'Транспорт', amount: '6000', category: 'transport', recurring: true, frequency: 'monthly', essential: true },
    { id: '5', name: 'Развлечения', amount: '10000', category: 'entertainment', recurring: true, frequency: 'monthly', essential: false }
  ],
  
  // Сбережения
  savings: {
    emergencyFund: {
      enabled: true,
      target: '180000', // 6 месяцев расходов
      current: '50000'
    },
    retirementFund: {
      enabled: false,
      target: '1000000',
      current: '0',
      monthlyContribution: '0'
    },
    goals: [
      { id: '1', name: 'Отпуск', target: '120000', current: '40000', deadline: '2023-12-31', priority: 'medium' }
    ]
  },
  
  // Инвестиции
  investments: {
    enabled: false,
    monthlyContribution: '0',
    portfolioValue: '0',
    expectedReturn: '7'
  },
  
  // Настройки распределения
  distribution: {
    savingsPercentage: 10,
    entertainmentPercentage: 10,
    essentialsPercentage: 70,
    investmentsPercentage: 10
  },
  
  // Дополнительные настройки
  settings: {
    includeInflation: true,
    inflationRate: '4', // годовой процент
    includeTaxes: true,
    taxRate: '13', // процент НДФЛ
    currency: 'RUB',
    roundNumbers: true
  }
};

// Определение категорий доходов
export const incomeCategories = [
  { id: 'salary', name: 'Зарплата', icon: 'Briefcase' },
  { id: 'business', name: 'Бизнес/ИП', icon: 'Building' },
  { id: 'side_job', name: 'Подработка', icon: 'Tool' },
  { id: 'rental', name: 'Аренда имущества', icon: 'Home' },
  { id: 'investments', name: 'Инвестиции', icon: 'TrendingUp' },
  { id: 'pension', name: 'Пенсия', icon: 'Heart' },
  { id: 'benefits', name: 'Социальные выплаты', icon: 'Award' },
  { id: 'other', name: 'Прочие доходы', icon: 'Plus' }
];

// Определение категорий расходов
export const expenseCategories = [
  { id: 'housing', name: 'Жилье', icon: 'Home', essential: true },
  { id: 'utilities', name: 'Коммунальные услуги', icon: 'Droplet', essential: true },
  { id: 'groceries', name: 'Продукты питания', icon: 'ShoppingCart', essential: true },
  { id: 'health', name: 'Здоровье/Медицина', icon: 'Heart', essential: true },
  { id: 'transport', name: 'Транспорт', icon: 'Car', essential: true },
  { id: 'communication', name: 'Связь/Интернет', icon: 'Wifi', essential: true },
  { id: 'clothing', name: 'Одежда', icon: 'ShoppingBag', essential: false },
  { id: 'entertainment', name: 'Развлечения', icon: 'Music', essential: false },
  { id: 'education', name: 'Образование', icon: 'BookOpen', essential: false },
  { id: 'restaurants', name: 'Кафе/Рестораны', icon: 'Coffee', essential: false },
  { id: 'gifts', name: 'Подарки', icon: 'Gift', essential: false },
  { id: 'travel', name: 'Путешествия', icon: 'Globe', essential: false },
  { id: 'subscriptions', name: 'Подписки', icon: 'Calendar', essential: false },
  { id: 'loans', name: 'Кредиты/Займы', icon: 'CreditCard', essential: true },
  { id: 'pets', name: 'Домашние животные', icon: 'Github', essential: false },
  { id: 'other', name: 'Прочие расходы', icon: 'Plus', essential: false }
];

// Определение регионов России
export const russianRegions = [
  { id: 'moscow', name: 'Москва', costsMultiplier: 1.5 },
  { id: 'spb', name: 'Санкт-Петербург', costsMultiplier: 1.3 },
  { id: 'city_millionaire', name: 'Город-миллионник', costsMultiplier: 1 },
  { id: 'medium_city', name: 'Средний город', costsMultiplier: 0.8 },
  { id: 'small_city', name: 'Малый город', costsMultiplier: 0.7 },
  { id: 'rural', name: 'Сельская местность', costsMultiplier: 0.6 }
];

/**
 * FamilyBudgetCalculatorPage Component - Полный калькулятор семейного бюджета с планированием доходов и расходов.
 */
const FamilyBudgetCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('family-budget-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор семейного бюджета | Gensio';
  const metaDescription = tool?.metaDescription || 'Полный калькулятор семейного бюджета: планирование доходов и расходов, финансовых целей, экономии, советы по распределению средств, шаблоны для разных типов семей и жизненных ситуаций.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [errors, setErrors] = useState({});
  const [savedBudgets, setSavedBudgets] = useState([]);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'income', 'expenses', 'savings'

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных бюджетов из localStorage ---
  useEffect(() => {
    const savedBudgetsData = localStorage.getItem('savedBudgets');
    if (savedBudgetsData) {
      try {
        setSavedBudgets(JSON.parse(savedBudgetsData));
      } catch (e) {
        console.error("Error loading saved budgets:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'family':
          newState.budgetType = 'family';
          newState.familyMembers = 2;
          break;
        case 'personal':
          newState.budgetType = 'personal';
          newState.familyMembers = 1;
          break;
        case 'family-with-children':
          newState.budgetType = 'family';
          newState.familyMembers = 3;
          newState.hasChildren = true;
          newState.childrenCount = 1;
          break;
        case 'pensioner':
          newState.budgetType = 'personal';
          newState.familyMembers = 1;
          // Добавляем пенсию как основной доход
          newState.incomes = [
            { id: '1', name: 'Пенсия', amount: '20000', category: 'pension', recurring: true, frequency: 'monthly' }
          ];
          break;
        case 'savings':
          newState.distribution = {
            ...newState.distribution,
            savingsPercentage: 20,
            entertainmentPercentage: 5,
            essentialsPercentage: 70,
            investmentsPercentage: 5
          };
          break;
        case 'investments':
          newState.investments.enabled = true;
          newState.investments.monthlyContribution = '10000';
          newState.distribution = {
            ...newState.distribution,
            savingsPercentage: 5,
            entertainmentPercentage: 5,
            essentialsPercentage: 70,
            investmentsPercentage: 20
          };
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setErrors({});
    
  }, [subToolId]);

  // Сохранение бюджета в localStorage
  const saveBudget = (name) => {
    if (!results) return;

    const newSavedBudget = {
      id: Date.now().toString(),
      name: name || formData.budgetName,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results }
    };

    const updatedSavedBudgets = [...savedBudgets, newSavedBudget];
    setSavedBudgets(updatedSavedBudgets);
    localStorage.setItem('savedBudgets', JSON.stringify(updatedSavedBudgets));

    return newSavedBudget;
  };

  // Удаление сохраненного бюджета
  const deleteSavedBudget = (id) => {
    const updatedSavedBudgets = savedBudgets.filter(budget => budget.id !== id);
    setSavedBudgets(updatedSavedBudgets);
    localStorage.setItem('savedBudgets', JSON.stringify(updatedSavedBudgets));
  };

  // Загрузка сохраненного бюджета
  const loadSavedBudget = (id) => {
    const budgetToLoad = savedBudgets.find(budget => budget.id === id);
    if (budgetToLoad) {
      setFormData(budgetToLoad.formData);
      setResults(budgetToLoad.results);
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Wallet className="w-8 h-8 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор семейного бюджета'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Quick Actions Bar --- */}
            <QuickActionsBar 
              formData={formData}
              setFormData={setFormData}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
            />

            {/* --- Форма калькулятора --- */}
            <BudgetForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              incomeCategories={incomeCategories}
              expenseCategories={expenseCategories}
              russianRegions={russianRegions}
              saveBudget={saveBudget}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <BudgetOverview
                  results={results}
                  formData={formData}
                />
                <IncomeExpenseBreakdown
                  results={results}
                  formData={formData}
                  incomeCategories={incomeCategories}
                  expenseCategories={expenseCategories}
                />
              </>
            )}

            {/* --- Советы по бюджету --- */}
            <BudgetTips results={results} formData={formData} />

            {/* --- Информационный контент о семейном бюджете --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель финансовых целей --- */}
            <FinancialGoalsSidebar 
              formData={formData}
              setFormData={setFormData}
              results={results}
            />

            {/* --- Панель сохраненных бюджетов --- */}
            <SavedBudgetsSidebar 
              savedBudgets={savedBudgets}
              deleteSavedBudget={deleteSavedBudget}
              loadSavedBudget={loadSavedBudget}
            />

            {/* --- Панель трендов и сравнения --- */}
            <TrendingSidebar 
              results={results}
              russianRegions={russianRegions}
            />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-emerald-600 focus:ring-emerald-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-emerald-600 focus:ring-emerald-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Budget Categories Styling */
        .category-icon {
          @apply flex-shrink-0 w-5 h-5 text-emerald-500 dark:text-emerald-400;
        }
        .category-name {
          @apply text-sm font-medium text-gray-700 dark:text-gray-300;
        }
        .income-item {
          @apply bg-emerald-50 dark:bg-emerald-900/20 border-emerald-100 dark:border-emerald-800;
        }
        .expense-item {
          @apply bg-red-50 dark:bg-red-900/20 border-red-100 dark:border-red-800;
        }
        .savings-item {
          @apply bg-blue-50 dark:bg-blue-900/20 border-blue-100 dark:border-blue-800;
        }
        
        /* Tab Styling */
        .budget-tab {
          @apply relative px-4 py-2 text-sm font-medium rounded-t-lg transition-colors focus:outline-none;
        }
        .budget-tab-active {
          @apply text-emerald-600 dark:text-emerald-400 border-b-2 border-emerald-500 dark:border-emerald-400;
        }
        .budget-tab-inactive {
          @apply text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300;
        }
      `}</style>
    </div>
  );
};

export default FamilyBudgetCalculatorPage;